import { Section, Sub, Card, Note, Warn, Fields, Code, New, Shot } from '../../../components/docs/Blocks';

export default function WorldEditor() {
  return (
    <Section
      id="world-editor"
      title="World Editor"
      lead="The world itself rather than its settings: terrain, the things standing on it, and the records Minecraft keeps about both. Ten screens, most of which read the whole world database, so several show a progress bar the first time you open them."
    >
      <Sub id="world-map" title={<>World Map <New /></>}>
        <Shot
          name="world-world.map"
          alt="The World Map with the pruning workspace open and two selection shapes drawn"
        />
        <Card>
          <p>
            The world drawn from its own blocks — every generated chunk, in whichever
            dimension you pick. Drag with the Pan tool to move around and the wheel to
            zoom; the readout under the cursor gives the chunk and block coordinates you
            are over.
          </p>
          <Fields
            items={[
              ['Dimensions', 'Overworld, Nether and End, plus any custom dimensions an add-on has created. Since 3.0.4 the map asks which one to start with and reads only that one; the others load when you switch to them.'],
              ['Layers', 'How the map is drawn. Slice at Y hides everything above a chosen height and renders the first visible block below it, which is how you see inside a base or a cave system. X-ray netherrack does what it says, and is on by default in the Nether because otherwise the Nether is a solid red rectangle.'],
              ['Right-click a chunk', 'Opens it in the Chunk Inspector.'],
              ['Markers', "Every player's last saved position is drawn as their head, and the world spawn has a marker of its own. Hover one for the exact coordinates. Since 3.0.3 the head is the player's real one, and clicking it opens that player in the Player Editor; the Identity card there has a button that comes back the other way and frames them on the map."],
              ['Leftover chunks', 'Since 3.1, chunks that hold data but no terrain — block entities, entities or ticks left behind — show as faint grey squares. Hovering one says so, and pruning an area removes them with the rest.'],
            ]}
          />
          <Note>
            A large world takes a while the first time — the map is built by reading
            every chunk record in the database. The progress line tells you how many
            chunks it has found, and the result is cached for the session.
          </Note>
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Biomes <New>New in 3.0.3</New>
        </h4>
        <Card>
          <p>
            <strong>Biomes</strong> opens a workspace like the pruning one, but what
            it stages is a biome change rather than a deletion. Draw an area, choose a
            biome, and every generated chunk inside the area is set to it when you
            save. The summary counts the chunks it will change and says how many in
            the area are already that biome.
          </p>
          <Fields
            items={[
              ['What changes', 'Only the biome. Mob spawning, grass and water colour, and whether it rains or snows follow the new biome; the blocks stay exactly as they are.'],
              ['What does not', "Chunks inside the area that have not been generated yet. They take the seed's biome when the game makes them, so load the area in-game first if you need those too."],
              ['One chunk at a time', "The Chunk Inspector's Overview has a Set biome picker for a single chunk, which is often all you need — a mob farm, say, or one patch that keeps snowing on a build."],
            ]}
          />
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Moving a custom dimension between worlds <New>New in 3.0.3</New>
        </h4>
        <Card>
          <p>
            A dimension an add-on has created can be lifted out of one world and
            dropped into another. <strong>Export Dimension</strong> is enabled when a
            custom dimension is showing on the map; it writes the terrain, entities
            and containers of that dimension to a <Code>.btdimension</Code> file
            along with the add-ons the source world had enabled.{' '}
            <strong>Import Dimension</strong> reads one back into the open world and
            shows you what it found before anything is staged.
          </p>
          <Fields
            items={[
              ['The review', 'Which dimension, which world it came from, how many chunks, entities and containers, and whether it will reuse a registration the destination already has or create a new one.'],
              ['Add-ons', 'Toolchest works out which of the source add-ons own the dimension. A pack that is already installed in Minecraft can be activated on the destination world as part of the import; one that is missing, or installed at a different version, is flagged as a warning so you know before you save.'],
              ['Refused', 'An import into a dimension slot the destination already uses is refused rather than merged. Marketplace content is never touched.'],
              ['Undo', 'The import and its add-on activations are one staged change: review it in the action bar, or unstage it, before you save.'],
              ['Missing entity records', 'Since 3.0.4, if chunks in the dimension point at entities the world no longer has, the export stops and lists them — how many, and which chunks. You can go on without them, which leaves only those dangling references out of the file. The source world is not changed either way.'],
            ]}
          />
          <Note>
            The Overworld, Nether and End cannot be exported — only dimensions a
            pack has added. Moving vanilla terrain between worlds is a different job.
          </Note>
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Pruning
        </h4>
        <Card>
          <p>
            <strong>Prune mode</strong> opens the selection workspace beside the map.
            Pruning deletes generated chunks so the world regenerates them fresh,
            which is how you reclaim disk space from places you explored once and
            never went back to.
          </p>
          <Fields
            items={[
              ['Rectangle and Circle tools', 'Draw as many shapes as you like. The summary counts the chunks inside them and the chunks outside.'],
              ['Resizing', 'New in 3.1. Drag the handles on a rectangle’s corners and sides, or on a circle’s edge, to resize a shape after drawing it. The counts update when you let go, and Escape cancels a resize. The same handles work in Biomes.'],
              ['Precise coordinates', 'Type exact block coordinates for a rectangle when you know the area you want.'],
              ['Invert', 'Flips the meaning of the selection: normally the shapes are what you keep, inverted they are what you remove.'],
              ['Stage pruning', 'Adds the deletion to your unsaved changes. Nothing is removed from the world until you save. Since 3.0.4 the save then compacts the world database, once the deletion is verified, so the disk space those chunks took is given back straight away.'],
              ['Stage dimension reset', 'Stages every chunk in the dimension, including ones the map cannot draw. The dimension regenerates from the seed next time it loads.'],
            ]}
          />
          <Warn>
            Pruned chunks lose everything in them: terrain, chests and their contents,
            every entity, pending ticks, the village records for that area and,
            since 3.1, the nether portal links for portals inside it. Players, level
            settings and the other dimensions are untouched, and so are records that
            describe the whole world rather than one place — the scoreboard and map
            items. A dimension reset asks you to confirm before it will even stage.
          </Warn>
        </Card>
      </Sub>

      <Sub id="chunk-inspector" title="Chunk Inspector">
        <Shot
          name="world-world.chunks"
          alt="The Chunk Inspector showing one chunk’s blocks, entities and tick data"
        />

        <Card>
          <p>
            One chunk at a time, in detail. Reach it from the map by right-clicking, or
            type chunk coordinates and use the compass arrows to walk around from
            there.
          </p>
          <Fields
            items={[
              ['Chunk view', 'A rendered preview of the chunk. Set a Y-level to cut a cross-section, or leave it on Surface for the top-down view. Hovering names the block under the cursor and its column.'],
              ['Overview', 'The dimension, the block range the chunk covers, which biomes are present, and how many pending and random ticks it carries. A Set biome picker changes the whole chunk to one biome when you save — the terrain stays as it is; spawning, colours and weather follow. A chunk with several biomes shows as Mixed until you pick one. To do an area at once, use Biomes on the World Map.'],
              ['Blocks', 'What the chunk is made of, counted by type. Primary block storage is counted separately from waterlogged and other secondary layers.'],
              ['Block Entities and Entities', 'What is standing in the chunk. Read-only here, with a link to the screen that edits them.'],
              ['Tick Data', 'The chunk\'s pending and random ticks, with block, position and time.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="block-search" title={<>Block Search <New /></>}>
        <Shot
          name="world-world.block_search"
          alt="The Block Search screen listing every deepslate coal ore found inside a coordinate range"
        />
        <Card>
          <p>
            Name a block and Toolchest finds every one of it in the generated
            terrain, with exact coordinates. It searches the blocks themselves, not
            their contents — a chest is found as a block at a position, and what is
            inside it belongs to{' '}
            <a href="#block-entities" className="text-primary-500 hover:text-primary-600 underline">Block Entities</a>.
            Nothing on this screen changes the world.
          </p>
          <Fields
            items={[
              ['Choosing a block', 'Start typing a name or an identifier — "coal", "deepslate", or a full id like minecraft:ancient_debris — and pick from the suggestions. The search starts as soon as you choose one.'],
              ['Dimension', 'All dimensions, or just one — including any a pack has added.'],
              ['Coordinate range', 'Optional inclusive bounds on X, Y and Z. Every bound is optional on its own, so you can give a Y range and leave X and Z open, or set only a floor. Apply range re-runs the search; Enter does the same; Clear drops back to the whole world.'],
              ['Results', 'Every match as its exact coordinates and dimension. Choosing one opens the detail pane, and Show on World Map frames and marks that exact block.'],
              ['Result limit', 'A search returns at most 5,000 matches. When that cap is reached the summary says so; narrow by dimension, by coordinate range, or search a rarer block.'],
              ['Export', 'New in 3.1. Saves every result as a CSV spreadsheet or a text file. The CSV has the columns x, y, z, dimension, block, identifier and layer. It holds the same results as the list, so the 5,000 cap applies to it too.'],
              ['Skipped subchunks', 'The summary counts any subchunk written in a format too old to read, or too damaged. Normally zero.'],
            ]}
          />
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Storage layers
        </h4>
        <Card>
          <p>
            Bedrock stores more than one block per position: the block itself, and
            a second layer for things like the water in a waterlogged fence. A match
            found on one of those extra layers is labelled with its layer number in
            the list and in the detail pane. No label means the ordinary one.
          </p>
          <p>
            This is why searching for water finds it inside waterlogged stairs and
            coral as well as in the sea.
          </p>
        </Card>
      </Sub>

      <Sub id="world-entities" title="Entities">
        <Card>
          <p>
            Every mob, item frame, boat, dropped item and armour stand in the world,
            grouped by type. Reading them means a full database scan, which the screen
            reports as it runs.
          </p>
          <Fields
            items={[
              ['Grouping and filter', 'Collapsed by type with a count on each, filterable by type or custom name.'],
              ['Details', 'Identity, position, health, custom name, tags, whether it is a baby, and which storage it lives in — modern entity records or the older in-chunk storage some worlds still use.'],
              ['Baby', 'Since 3.1, turning Baby off grows a mob up the way the game does, and turning it on makes an adult a baby again, for 30 mobs including cows, sheep, pigs, chickens, wolves, cats, horses and foxes. Mobs whose age Minecraft cannot change this way, such as villagers and zombies, show their age without a switch.'],
              ['NBT data', 'The full raw record for the selected entity, editable.'],
              ['Show on Map', 'Frames and marks the entity on the World Map.'],
              ['Open Trades', 'For a villager, jumps straight to its trade offers.'],
              ['Delete', 'One entity, or every entity of a type at once. Staged, and undoable from the review sheet before you save.'],
            ]}
          />
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Copying a mob to another world <New>New in 3.1</New>
        </h4>
        <Shot
          name="world-world.entities-copied"
          alt="The Entities screen with a copied entity waiting in the clipboard card, beside the Paste button"
        />
        <Card>
          <p>
            The copy button on an entity — or <strong>Copy</strong> on a pet in{' '}
            <a href="#player-tamed" className="text-primary-500 hover:text-primary-600 underline">Tamed Pets</a>{' '}
            — puts it on the entity clipboard, a card at the top of Entities that
            says what it holds and which world it came from. It stays there in
            another world, and after closing Toolchest, until you clear it.
          </p>
          <p>
            <strong>Paste…</strong> asks which player it should appear next to.
            When you save, it arrives at their feet, in the dimension they are in,
            with its name, health, armour and everything else intact. A pet becomes
            that player&rsquo;s pet. The dialog warns you if the player is in the
            air, if the entity came from a newer version of Minecraft, or if it
            needs an add-on this world does not have.
          </p>
        </Card>
      </Sub>

      <Sub id="block-entities" title={<>Block Entities <New /></>}>
        <Shot
          name="world-world.block_entities"
          alt="The Block Entities screen listing chests, signs and spawners by type"
        />

        <Card>
          <p>
            Blocks that carry data of their own: chests, barrels, shulker boxes,
            hoppers, furnaces, signs, banners, beehives, spawners, command blocks,
            lecterns, jukeboxes and the rest. Version 2 could not see these at all.
          </p>
          <Fields
            items={[
              ['The list', 'Every record in the world grouped by type, with its exact block position and the chunk it belongs to.'],
              ['Details', 'Type, position, storage and how many entries the chunk record holds.'],
              ['NBT data', 'The raw record, editable — which is how you change what is in a chest, what a sign says, or what a spawner spawns.'],
              ['Show on Map', 'Frames and marks the block on the World Map.'],
            ]}
          />
          <Note>
            Records that cannot be decoded are counted and skipped rather than
            stopping the scan, so one damaged chest does not hide the other four
            thousand.
          </Note>
        </Card>
      </Sub>

      <Sub id="world-properties" title={<>Properties &amp; Scoreboards <New /></>}>
        <Shot
          name="world-world.properties"
          alt="The Properties and Scoreboards screen showing world dynamic properties grouped by add-on"
        />
        <Card>
          <p>
            Two things Minecraft stores about the world rather than about any one
            place in it: the custom data add-ons keep, and the scoreboard. Both
            live behind a tab strip on the same screen.
          </p>
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Dynamic properties
        </h4>
        <Card>
          <p>
            The world-level equivalent of the player properties in the Player
            Editor — values an add-on set on the world itself rather than on
            anybody in it. Quest progress, shop stock, a minigame’s state.
          </p>
          <Fields
            items={[
              ['Grouped by add-on', 'Each group is the pack that wrote it, resolved to a readable name where Toolchest can find one. A group whose pack has been removed still shows, with its id.'],
              ['Editing', 'Text, number and true-or-false values can be changed in place. Anything whose shape Toolchest does not recognise is shown but left alone — the Advanced screen can still reach it.'],
              ['Search', 'Filters by key or value, which is what you want on a world carrying hundreds of them.'],
              ['Remove', 'One property, everything one add-on wrote, or every world property at once.'],
            ]}
          />
          <Warn>
            These values are how an add-on remembers what it was doing. Removing
            them can reset a quest, empty a shop or break a minigame — Toolchest
            says so before it stages the removal.
          </Warn>
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Scoreboards
        </h4>
        <Shot
          name="world-world.properties-scoreboards"
          alt="The Scoreboards pane showing an objective, its display slot and the scores under it"
        />
        <Card>
          <p>
            Every objective the world carries, with the scores under it — the same
            data <Code>/scoreboard</Code> manages, without the commands.
          </p>
          <Fields
            items={[
              ['Objectives', 'Display name, internal name and criteria, with a count of the scores each holds.'],
              ['Display slots', 'Where an objective is shown in game — sidebar, list or below the name — and the order it sorts in. An objective assigned to no slot says so.'],
              ['Scores', 'Each participant and their score, editable in place. Participants are players or entities; players are resolved to a name where the world knows one, and anything else keeps its scoreboard id.'],
              ['Remove', 'A single score, every score on an objective, the objective itself, or all objectives at once.'],
            ]}
          />
          <Note>
            Removing an objective also clears its display slots, and drops any
            participant that no longer appears on another objective — so the
            scoreboard is left consistent rather than carrying identities nothing
            points at. A score Toolchest cannot make sense of is shown and left
            untouched rather than guessed at.
          </Note>
        </Card>
      </Sub>

      <Sub id="villager-trades" title="Villager Trades">
        <Shot
          name="world-world.trades-editor"
          alt="A villager’s trade offers grouped by career tier, with the item editor open"
        />

        <Card>
          <p>
            Every villager in the world with trade data, and full editing of what they
            offer. Trades are grouped by career tier — Novice, Apprentice, Journeyman,
            Expert, Master — so you can see which offers belong to which level.
          </p>
          <Fields
            items={[
              ['The villager list', 'Name, profession, career level, position and dimension, sortable and filterable.'],
              ['Profession and biome', "Change either. The biome variant changes the villager's appearance to match — Plains, Desert, Jungle, Savanna, Snow, Swamp or Taiga."],
              ['Editing an offer', 'Change the cost item, the result item and the quantities. Each item gets the full item editor: custom name, enchantments, armour trim and the unbreakable flag.'],
              ['Second cost item', 'Add or remove a second required item, turning a one-item trade into a two-item one.'],
              ['Maximum uses', 'How many times the offer can be taken before it locks. Set it to -1 for unlimited.'],
              ['Add and remove trades', 'Add a copy of the last offer at a tier, ready to edit, or remove one. Both are staged and undoable.'],
            ]}
          />
          <Note>
            An item that cannot legitimately carry enchantments, a trim or the
            unbreakable flag starts clean when you swap to it, rather than inheriting
            them from the item it replaced.
          </Note>
        </Card>
      </Sub>

      <Sub id="villages" title={<>Villages <New /></>}>
        <Shot
          name="world-world.villages"
          alt="The Villages screen listing villages with dweller and bed counts, and a village summary"
        />
        <Card>
          <p>
            The village records Minecraft keeps — which villages exist, where, who
            lives in them and what they have claimed. Nothing in version 2 read any of
            this, and it is the screen that answers ”why has my village stopped
            breeding”.
          </p>
          <Fields
            items={[
              ['Summary', 'Bounds, centre, size, how many chunks the village covers and how many of those still exist, plus the village\'s own timers — the village tick, and the bed, golem and claim scans. The centre is given as X, Y and Z and the size as width × height × length, because a village is a box rather than a footprint: the centre is the point the game measures from when it decides where cats and iron golems can spawn.'],
              ['Dwellers', 'Villagers, iron golems, raiders and cats, each with when they last worked. A dweller naming an entity that is no longer in the world is flagged: the village still counts it, and that is what stops it breeding or restocking.'],
              ['Claims', 'Beds, workstations and the meeting point, each marked claimed or free — and marked wrong when the block that was claimed is no longer there. A claim held by a villager the village no longer lists is never freed and no new villager can take it; those are flagged and can be released.'],
              ['Reputation', "Each player's standing with the village."],
              ['Raid', 'If a raid is running: its state, how long it has been going, the wave, the raiders and the spawn point. It can be ended.'],
              ['Show on the map', 'Switches dimension, frames the village and marks it.'],
            ]}
          />
          <Note>
            <strong>Abandoned villages</strong> are villages standing on chunks that no
            longer exist — usually the residue of pruning. The records survived the
            terrain and still claim beds and workstations at coordinates that have
            regenerated as something else. They can be deleted individually or all at
            once.
          </Note>
        </Card>
      </Sub>

      <Sub id="world-tools" title="World Tools">
        <Card>
          <p>
            Utilities for world data that does not need a screen of its own. Today
            that means the Ender Dragon.
          </p>
          <Fields
            items={[
              ['Current state', 'Whether the dragon is alive, dead, never spawned or respawning, and where the exit portal is.'],
              ['Reset Dragon Fight', 'Puts the fight back to its initial state, as though The End had never been visited. The dragon respawns naturally the next time you enter The End. If the Ender Dragon mob event had been disabled, resetting re-enables it and says so.'],
            ]}
          />
          <Note>
            A world whose End has never been visited has no dragon data to show, and
            the screen says so rather than showing an empty panel.
          </Note>
        </Card>
      </Sub>

      <Sub id="world-maps" title="Maps">
        <Shot
          name="world-world.maps"
          alt="The Maps screen with a rendered map preview and its zoom-level hierarchy"
        />

        <Card>
          <p>
            Every map item in the world, with its rendered image. Maps are one of the
            largest things a world accumulates, and a world with hundreds of them is
            mostly map data.
          </p>
          <Fields
            items={[
              ['The list', 'Grouped by map, with the zoom-level hierarchy expandable underneath — a single map at full zoom carries several records. Filter by id or coordinates, and by dimension.'],
              ['Preview', 'The map as it looks in the game. Blank maps and records that cannot be decoded safely are labelled rather than drawn.'],
              ['Details', 'Map id, centre, scale, whether it is locked, coverage, and how much space the record and its hierarchy take.'],
              ['Delete', 'Stage a map and all its zoom levels for deletion, or stage every map in the world at once. Individual maps can be unstaged again before you save.'],
              ['Import Image', 'Turns a PNG or JPEG into a new locked map item, with a 128x128 preview before you commit, fitted or cropped to the map, and placed in a chosen player\'s first free inventory slot. Transparent pixels show as unexplored.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="world-structures" title="Structures">
        <Shot
          name="world-world.structures"
          alt="The Structures screen with a 3D preview and the block list to rebuild it"
        />

        <Card>
          <p>
            The structures saved in the world with structure blocks, and the records
            add-ons store in the same place.
          </p>
          <Fields
            items={[
              ['The list', 'Name and namespace, kind, size and record size. Filterable by namespace and by kind — a build, an add-on record, or an empty one.'],
              ['3D preview', 'The structure rendered in the app, rotatable, with a slider to cut away everything above a height and a lighting toggle. Blocks with no known colour are drawn grey and counted.'],
              ['Blocks used', 'The full material list, given in stacks — what it would take to build it again.'],
              ['Details', 'Size in x, y and z, the world position it was saved from, palette entries, and how many block entities and entities it carries.'],
              ['Export', 'Writes the raw record to an .mcstructure file, which any Bedrock world can load. Structures can be exported one at a time or all at once to a folder.'],
              ['Import', 'Reads an .mcstructure file in as a new saved structure, with its namespace and name set by you. A name that already exists warns that importing will overwrite it.'],
              ['Rename', 'The name is the record key, so renaming writes a new record and removes the old one — both together when you save.'],
              ['Show where it was saved', 'Frames the origin on the World Map.'],
            ]}
          />
          <Warn>
            Add-ons store their data in structure records. Toolchest marks those as
            add-on data and warns before staging one for deletion, because deleting it
            can break the add-on that wrote it.
          </Warn>
        </Card>
      </Sub>

      <Sub id="tick-data" title="Tick Data">
        <Shot
          name="world-world.ticks"
          alt="The Tick Data screen showing ticking areas and the area editor"
        />

        <Card>
          <p>
            The update queues Minecraft keeps, and the ticking areas you have defined.
            Not loaded until you ask for it, because finding it means scanning the
            database.
          </p>
          <Fields
            items={[
              ['Ticking Areas', 'The areas created with /tickingarea, with their name, dimension, shape and bounds. They can be edited, added and deleted — Minecraft allows a fixed number per world and Toolchest enforces it rather than letting you create one the game will drop.'],
              ['Pending Ticks', 'Chunks with scheduled block updates waiting to run, counted per chunk.'],
              ['Random Ticks', 'Chunks with random tick entries queued.'],
              ['Deleting', 'One chunk, everything in a dimension, or everything of a kind. Anything staged can be restored before you save.'],
              ['Inspect Chunk', 'Opens the selected chunk in the Chunk Inspector.'],
            ]}
          />
          <Note>
            Removing a row clears that chunk’s whole update queue. Minecraft recreates
            ticks as the world runs, so this is a way to clear a stuck queue rather
            than a permanent change.
          </Note>
        </Card>
      </Sub>

    </Section>
  );
}
